import { BoardAreaEnum, SAPApplicationParameters } from '@platform/client/schemas';
import { FormsInputDropdown, RadioGroup } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import { TeamShowcase } from './TeamShowcase';

type FormValues = {
  boardArea: string;
  workType: 'full-time' | 'part-time';
};

const BOARD_AREA_OPTIONS = [
  { text: 'Customer Value Group', textValue: BoardAreaEnum['customer-value-group'] },
  { text: 'Finance & Administration', textValue: BoardAreaEnum['finance-administration'] },
  { text: 'Office of the CEO', textValue: BoardAreaEnum['ceo-office'] },
  { text: 'People & Culture', textValue: BoardAreaEnum['people-culture'] },
  { text: 'SAP Product & Engineering', textValue: BoardAreaEnum['product-engineering'] },
  { text: 'Strategy & Operations', textValue: BoardAreaEnum['strategy-operations'] },
];

export const SAPWorkExperience = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<SAPApplicationParameters>(dataKey);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      workType: getApplicationDataByKey('workType'),
      boardArea: getApplicationDataByKey('boardArea') || '',
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({ boardArea: data.boardArea as BoardAreaEnum, workType: data.workType });
    onContinue();
  };

  const handleBack = () => {
    updateApplicationData({
      boardArea: watch('boardArea') as BoardAreaEnum,
      workType: watch('workType'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      customLeftPanelContent={
        <TeamShowcase
          title={
            <div className='text-display-lg'>
              You’re part of a global <br /> movement at SAP
            </div>
          }
          description=''
        />
      }
    >
      <ApplicationPageContent
        title='Tell us about your work at SAP'
        description="
          Your company wants to know these details as part of your application, but they won't appear
          on your profile."
      >
        <FormsInputDropdown
          control={control}
          name='boardArea'
          rules={{
            required: 'Select your board area to continue',
          }}
          label='Which board are you most closely associated with?'
          placeholder='Select one'
          items={BOARD_AREA_OPTIONS}
          type='default'
          error={errors.boardArea}
          inputDisabled={true}
        />
        <RadioGroup
          name='sap-work-experience'
          aria-label='sap-work-experience'
          items={[
            {
              value: 'full-time',
              title: 'Full time, permanent employee',
            },
            {
              value: 'part-time',
              title: 'Part time, 30 hours/week',
            },
          ]}
          onChange={(value) => {
            setValue('workType', value as 'full-time' | 'part-time');
          }}
          register={() => {
            return register('workType', {
              required: 'Select your employment type to continue',
            });
          }}
          defaultValue={getApplicationDataByKey('workType')}
          error={errors.workType?.message}
        />
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
